import {PaymentDetails, PaymentType} from "@bounce/shared/consumer/declarations";
import {useIsValidAmount} from "@bounce/shared/consumer/helpers";
import {CustomPaymentBox, PaymentContext, TermsType} from "@bounce/shared/consumer/pages/payment";
import {formatCurrencyApplePay, subtract} from "@bounce/shared/helpers";
import {CustomDialog} from "@bounce/shared/consumer/bounce-ui";
import {isUndefined} from "lodash";
import {generatePath, useNavigate} from "react-router-dom";
import {toast} from "sonner";
import {useAddPaymentMethod, useSinglePayment, useGetDashboardQuery} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {useAmplitude} from "~/contexts";
import {openPaymentFailedDialog} from "~/pages/account/payment/utils";
import {urls} from "~/routes/consumerUrls";
import NiceModal, {NiceModalHocProps, useModal} from "@ebay/nice-modal-react";
import currency from "currency.js";
import React, {useCallback, useEffect, useState} from "react";

import PaymentMethod from "./PaymentMethod";

interface IOneTimePaymentDialog extends NiceModalHocProps {
  planBalance: number;
  accountId: string;
  activePaymentPlanId?: string;
  pendingAmount: number;
}

const OneTimePaymentDialog = NiceModal.create(
  ({accountId, planBalance, activePaymentPlanId, pendingAmount}: IOneTimePaymentDialog) => {
    const {pageViewEvent, pageClickEvent} = useAmplitude();
    const navigate = useNavigate();
    const modal = useModal();

    const [buttonTouched, setButtonTouched] = useState(false);
    const [amount, setAmount] = useState<number>();

    const isValid = useIsValidAmount(amount, planBalance);
    const userDashboard = useGetDashboardQuery(
      {
        account_id: accountId,
        payment_plan_id: activePaymentPlanId,
      },
      {enabled: !!activePaymentPlanId}
    );
    const cardDetails = userDashboard.data?.last_4_digits;

    const closeHandler = useCallback((): void => {
      modal.resolve(false);
      modal.hide();
    }, []);

    const paymentState = useSinglePayment();
    const changePaymentMethod = useAddPaymentMethod({refetchType: "active"});
    const changePaymentMethodHandler = useCallback(
      async (paymentType: PaymentType, paymentDetails: PaymentDetails): Promise<void> => {
        let applePayResult = false;
        try {
          const result = await changePaymentMethod.mutateAsync({
            ...paymentDetails,
            account_id: accountId,
            payment_plan_id: userDashboard.data?.payment_plan_id,
          });
          if (result.success) {
            toast.success("Changes to your payment method were saved");
            applePayResult = true;
          } else {
            toast.error(`Failed to update payment method - ${result.error_message}`);
            setButtonTouched(false);
            applePayResult = false;
          }
        } catch (e) {
          toast.error("Failed to change your payment method");
          applePayResult = false;
        } finally {
          if (paymentType === "ApplePay" && !!paymentDetails.applePayCompletionFunction) {
            paymentDetails.applePayCompletionFunction(applePayResult);
          }
        }
      },
      [accountId, changePaymentMethod, userDashboard.data]
    );

    const paymentHandler = useCallback(
      async (
        paymentType: PaymentType,
        paymentDetails: PaymentDetails,
        isChangePaymentMethod: boolean
      ): Promise<void> => {
        let error: string | undefined;
        let applePayResult = false;
        try {
          await paymentState.mutateAsync({
            ...paymentDetails,
            account_id: accountId,
            amount: `${amount}`,
            is_partial_payment: true,
          });
          if (isChangePaymentMethod) {
            await changePaymentMethodHandler(paymentType, paymentDetails);
          }
          await paymentSuccessHandler(paymentType);
          applePayResult = true;
        } catch (err: any) {
          error = (err?.message as string) || "Unknown error occurred";
          const error_reason = (err?.reason as string) || "Unknown error occurred";
          const error_explanation = (err?.explanation as string) || "Unknown error occurred";
          openPaymentFailedDialog(error, error_reason, error_explanation, err?.origin, err?.code);
          paymentFailedHandler();
          applePayResult = false;
        } finally {
          if (paymentType === "ApplePay" && !!paymentDetails.applePayCompletionFunction) {
            paymentDetails.applePayCompletionFunction(applePayResult);
          }
          pageClickEvent({
            page: Pages.ONE_TIME_PAYMENT_DIALOG,
            cta: "pay_it_now",
            data: {
              contact: true,
              amount: currency(amount || 0).divide(100).value,
              payment_method: paymentType,
              error,
              is_prepaid: paymentDetails.is_prepaid,
            },
          });
        }
      },
      [accountId, paymentState]
    );

    const totalBalanceWithPending = subtract(planBalance, pendingAmount);
    const paymentSuccessHandler = useCallback(async (paymentType: PaymentType): Promise<void> => {
      closeHandler();
      navigate(generatePath(urls.paymentSuccess.url, {accountId}), {
        state: {isActivePaymentPlan: true, isPaymentSubmitted: paymentType === "ACH"},
      });
    }, []);

    const paymentFailedHandler = useCallback((): void => {
      setButtonTouched(false);
    }, []);

    useEffect(() => {
      pageViewEvent({page: Pages.ONE_TIME_PAYMENT_DIALOG});
    }, []);

    return (
      <PaymentContext.Provider
        value={{
          buttonTouched,
          setButtonTouched,
          isLoading: paymentState.isLoading,
        }}
      >
        <CustomDialog title={"One-time payment"}>
          <CustomPaymentBox
            amount={amount}
            isValid={isValid}
            totalBalance={totalBalanceWithPending}
            setAmount={setAmount}
          />
          <PaymentMethod
            accountId={accountId}
            paymentHandler={paymentHandler}
            lastDigitsCard={cardDetails}
            termsAndConditionsId={TermsType.SHORT_PAYMENT}
            disable={isUndefined(amount) || !isValid}
            currentMethodType={userDashboard.data?.payment_method_type}
            page={Pages.ONE_TIME_PAYMENT_DIALOG}
            paymentPlanId={activePaymentPlanId}
            immediatePaymentAmount={formatCurrencyApplePay(amount)}
          />
        </CustomDialog>
      </PaymentContext.Provider>
    );
  }
);

export default OneTimePaymentDialog;
